// src/components/CompanyAnalytics.js
import React, { useState, useEffect, useMemo } from "react";
import { Line, Pie, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Tooltip,
  Legend,
  BarElement,
} from "chart.js";
import { useNavigate, useLocation } from "react-router-dom";
import { FiArrowLeft } from "react-icons/fi";
import { getCompanyData } from "../../Service/apiService";
import "../../assets/css/Analytics.css"; // Reuse same CSS
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css"; 

// Register BarElement for Bar chart
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Tooltip,
  Legend
);

const toLower = (val) => (val || "").toLowerCase().trim();

const parseDate = (str) => {
  if (!str) return null;
  const date = new Date(str);
  return isNaN(date) ? null : date;
};

export default function CompanyAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();

  const [timeRange, setTimeRange] = useState("1y");
  const [selectedCategory, setSelectedCategory] = useState("total");
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Fetch companies
  useEffect(() => {
    const fetchCompanies = async () => {
      setLoading(true);
      try {
        const response = await getCompanyData("all");
        const raw = Array.isArray(response?.data) ? response.data : [];
        // Exclude internal
        const filtered = raw.filter(c => toLower(c.companyType) !== "internal");
        setCompanies(filtered);
      } catch (err) {
        console.error("Error fetching companies:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  // Compute date range
  const { computedStartDate, computedEndDate } = useMemo(() => {
    const now = new Date();
    if (timeRange === "custom") {
      const start = customStartDate
        ? new Date(customStartDate)
        : new Date(now.getFullYear() - 1, now.getMonth(), 1);
      const end = customEndDate ? new Date(customEndDate) : now;
      return { computedStartDate: start, computedEndDate: end };
    } else {
      let start;
      switch (timeRange) {
        case "1w":
          start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case "1m":
          start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          break;
        case "3m":
          start = new Date(now.getFullYear(), now.getMonth() - 3, 1);
          break;
        case "6m":
          start = new Date(now.getFullYear(), now.getMonth() - 6, 1);
          break;
        case "1y":
        default:
          start = new Date(now.getFullYear() - 1, now.getMonth(), 1);
      }
      return { computedStartDate: start, computedEndDate: now };
    }
  }, [timeRange, customStartDate, customEndDate]);

  // Categorize companies
  const categorized = useMemo(() => {
    if (companies.length === 0) return {};

    const total = companies.filter(c => toLower(c.companyType) !== "internal");
    const newCompanies = companies.filter(
      c =>
        toLower(c.companyType) === "new" &&
        c.invoiceDate &&
        toLower(c.companyStatus) === "active"
    );
 const renewed = companies.filter(
  c =>
    toLower(c.companyType) === "renewal" &&
    toLower(c.paymentStatus) === "paid" &&
    toLower(c.companyStatus) === "active" &&
    c.invoiceDate
);
    const lost = companies.filter(c => toLower(c.companyType) === "lost");
    const active = companies.filter(
      c =>
        c.invoiceDate &&
        toLower(c.companyStatus) === "active" &&
        toLower(c.companyType) !== "lost" &&
        toLower(c.companyType) !== "internal"
    );
    const unpaid = companies.filter(
      c =>
        toLower(c.paymentStatus) === "unpaid" &&
        toLower(c.companyStatus) === "active"
    );
    const trial = companies.filter(
      c =>
        toLower(c.companyType) === "trial" &&
        toLower(c.paymentStatus) === "trial" &&
        toLower(c.companyStatus) === "active"
    );

    return { total, new: newCompanies, renewed, lost, active, unpaid, trial };
  }, [companies]);

  // Color mapping — matching your CSS class names & pie style
  const COLORS = {
    total: "rgba(2, 37, 68, 1)",
    new: "rgb(42, 86, 130)",
    renewed: "rgb(66, 111, 155)",
    lost: "rgba(93, 114, 135, 1)",
    active: "rgba(62, 106, 150, 1)",
    unpaid: "rgba(168, 172, 177, 1)",
    trial: "rgba(97, 134, 171, 1)",
  };

  // Pie data (no "total")
  const pieData = useMemo(() => {
    const labels = ["New", "Renewed", "Lost", "Active", "Unpaid", "Trial"];
    const keyMap = {
      New: "new",
      Renewed: "renewed",
      Lost: "lost",
      Active: "active",
      Unpaid: "unpaid",
      Trial: "trial",
    };
    const data = labels.map(label => categorized[keyMap[label]]?.length || 0);
    const backgroundColors = labels.map(label => COLORS[keyMap[label]]);

    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: backgroundColors,
          borderColor: "#fff",
          borderWidth: 1,
        },
      ],
    };
  }, [categorized]);

  // Get relevant date for time-series
  const getDateForCategory = (company, category) => {
  const { createdAt, invoiceDate, subEndDate } = company;
  switch (category) {
    case "new":
    case "trial":
      return parseDate(invoiceDate) || parseDate(createdAt);
    case "renewed":
      // Use invoiceDate of renewal (assume renewed companies have updated invoiceDate on renewal)
      return parseDate(invoiceDate) || parseDate(subEndDate);
    case "lost":
      return parseDate(subEndDate) || parseDate(createdAt);
    case "unpaid":
      return parseDate(invoiceDate) || parseDate(createdAt);
    case "total":
    case "active":
    default:
      return parseDate(invoiceDate) || parseDate(createdAt);
  }
};

  // Time-series chart data
 // Time-series chart data
const timeChartData = useMemo(() => {
  if (!categorized[selectedCategory]?.length) {
    return { labels: [], datasets: [] };
  }

  const now = computedEndDate;
  const months = [];
  let current = new Date(computedStartDate);
  current.setDate(1);

  while (current <= now) {
    months.push(new Date(current));
    current.setMonth(current.getMonth() + 1);
  }

  const monthLabels = months.map((m) =>
    m.toLocaleString("default", { month: "short", year: "numeric" })
  );

  let data;

  // ✅ FIX: "active" = state over time → count by subscription overlap
  if (selectedCategory === "active") {
    data = months.map((monthStart) => {
      const monthEnd = new Date(monthStart);
      monthEnd.setMonth(monthEnd.getMonth() + 1);
      monthEnd.setDate(0); // last day of month

      return companies.filter((c) => {
        // Skip non-active, lost, or internal
        if (
          toLower(c.companyType) === "internal" ||
          toLower(c.companyType) === "lost" ||
          toLower(c.companyStatus) !== "active"
        ) {
          return false;
        }

        // Get subscription period
        const subStart = parseDate(c.subStartDate) || parseDate(c.invoiceDate);
        const subEnd = parseDate(c.subEndDate) || new Date(8640000000000); // far future

        // Skip if no start date
        if (!subStart) return false;

        // Overlap: [subStart, subEnd] ∩ [monthStart, monthEnd] ≠ ∅
        return subStart <= monthEnd && subEnd >= monthStart;
      }).length;
    });
  }
  // 📈 For "total": cumulative count of companies joined by month-end
  else if (selectedCategory === "total") {
    const joinDates = companies
      .map((c) => getDateForCategory(c, "total"))
      .filter((d) => d !== null);

    data = months.map((monthStart) => {
      const monthEnd = new Date(monthStart);
      monthEnd.setMonth(monthEnd.getMonth() + 1);
      monthEnd.setDate(0);
      return joinDates.filter((joinDate) => joinDate <= monthEnd).length;
    });
  }
  // 📊 For event-based categories (new, renewed, lost, etc.)
  else {
    const monthlyMap = {};
    const compsInCat = categorized[selectedCategory];
    compsInCat.forEach((c) => {
      const date = getDateForCategory(c, selectedCategory);
      if (!date || date < computedStartDate || date > now) return;

      const monthKey = date.toLocaleString("default", {
        month: "short",
        year: "numeric",
      });
      monthlyMap[monthKey] = (monthlyMap[monthKey] || 0) + 1;
    });
    data = monthLabels.map((label) => monthlyMap[label] || 0);
  }

  const labelMap = {
    total: "Total Companies (Cumulative)",
    new: "New Companies",
    renewed: "Renewed Companies",
    lost: "Lost Companies",
    active: "Active Companies (Monthly)",
    unpaid: "Unpaid Companies",
    trial: "Trial Companies",
  };

  const isLine = selectedCategory === "total" || selectedCategory === "active";

  return {
    labels: monthLabels,
    datasets: [
      {
        label: labelMap[selectedCategory] || "Companies",
        data,
        borderColor: COLORS[selectedCategory],
        backgroundColor: isLine
          ? `${COLORS[selectedCategory]}20`
          : COLORS[selectedCategory],
        tension: isLine ? 0.3 : undefined,
        pointRadius: isLine ? 3 : undefined,
        pointBackgroundColor: isLine ? COLORS[selectedCategory] : undefined,
        pointBorderColor: isLine ? "#fff" : undefined,
        pointBorderWidth: isLine ? 1 : undefined,
        // Ensure correct chart type per dataset
        type: isLine ? "line" : "bar",
      },
    ],
  };
}, [companies, categorized, selectedCategory, computedStartDate, computedEndDate]);

  // Handle pie click
  const handlePieClick = (elements) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const keys = ["new", "renewed", "lost", "active", "unpaid", "trial"];
      setSelectedCategory(keys[index]);
    }
  };

  // Back navigation
  const handleBack = () => {
    navigate("/analytics");
  };

  // Time range options
  const timeOptions = [
    { label: "1 Week", value: "1w" },
    { label: "1 Month", value: "1m" },
    { label: "3 Months", value: "3m" },
    { label: "6 Months", value: "6m" },
    { label: "1 Year", value: "1y" },
    { label: "Custom", value: "custom" },
  ];

  // Chart options
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { font: { size: 11 }, color: "#333" } },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "rgb(5, 55, 99)",
        bodyColor: "#333",
        borderColor: "#eee",
        borderWidth: 1,
        padding: 10,
        borderRadius: 6,
      },
    },
    onClick: (_evt, elements) => handlePieClick(elements),
    onHover: (evt, elements) => {
      evt.native.target.style.cursor = elements.length > 0 ? "pointer" : "default";
    },
  };
  const totalYAxisMin = useMemo(() => {
  if (selectedCategory !== "total") return 0;

  const values = timeChartData?.datasets?.[0]?.data || [];
  if (!values.length) return 0;

  const minValue = Math.min(...values);

  // buffer of 200, never go below 0
  return Math.max(minValue - 200, 0);
}, [selectedCategory, timeChartData]);


 const lineOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { labels: { font: { size: 11 }, color: "#333" } },
    tooltip: {
      backgroundColor: "#fff",
      titleColor: "rgb(5, 55, 99)",
      bodyColor: "#333",
      borderColor: "#eee",
      borderWidth: 1,
      padding: 10,
      borderRadius: 6,
    },
  },
  scales: {
    x: {
      title: {
        display: true,
        text: "Month",
        color: "#666",
        font: { size: 12, weight: "bold" },
      },
      grid: { color: "rgba(0,0,0,0.04)" },
      ticks: { color: "#666", font: { size: 11 } },
    },
    y: {
      title: {
        display: true,
        text: "Number of Companies",
        color: "#666",
        font: { size: 12, weight: "bold" },
      },
      beginAtZero: false, // 🔥 important
      min: selectedCategory === "total" ? totalYAxisMin : 0,
      grid: { color: "rgba(0,0,0,0.04)" },
      ticks: {
        color: "#666",
        font: { size: 11 },
        precision: 0,
      },
    },
  },
};


  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { font: { size: 11 }, color: "#333" } },
      tooltip: {
        backgroundColor: "#fff",
        titleColor: "rgb(5, 55, 99)",
        bodyColor: "#333",
        borderColor: "#eee",
        borderWidth: 1,
        padding: 10,
        borderRadius: 6,
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Month", color: "#666", font: { size: 12, weight: "bold" } },
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#666", font: { size: 11 } },
      },
      y: {
        title: { display: true, text: "Number of Companies", color: "#666", font: { size: 12, weight: "bold" } },
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.04)" },
        ticks: { color: "#666", font: { size: 11 }, precision: 0 },
      },
    },
  };

  const selectedCategoryLabel = useMemo(() => {
    const labelMap = {
      total: "Total Companies",
      new: "New Companies",
      renewed: "Renewed Companies",
      lost: "Lost Companies",
      active: "Active Companies",
      unpaid: "Unpaid Companies",
      trial: "Trial Companies",
    };
    return labelMap[selectedCategory] || "Companies";
  }, [selectedCategory]);

  const totalCompanyCount = categorized.total?.length || 0;

  //for skeleton
  const CompanyAnalyticsSkeleton = () => (
  <div className="analytics-content">
    {/* Pie chart skeleton */}
    <div className="chart-panel chart-panel--with-total">
      <Skeleton height={22} width={180} style={{ marginBottom: 12 }} />
      <Skeleton height={28} width={120} style={{ marginBottom: 16 }} />
      <Skeleton height={260} borderRadius={12} />
    </div>

    {/* Trend chart skeleton */}
    <div className="chart-panel">
      <Skeleton height={22} width={220} style={{ marginBottom: 16 }} />
      <Skeleton height={300} borderRadius={12} />
    </div>
  </div>
);

  return (
    <div className="analytics-container">
      <div className="header-section">
       

        <h1 className="title">Company Analytics</h1>
        <div className="time-filter-container">
          <select
            className="time-range-select"
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
          >
            {timeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {timeRange === "custom" && (
            <div className="custom-date-picker">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="date-input"
              />
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="date-input"
              />
            </div>
          )}
        </div>
      </div>

      {loading ? (
         <CompanyAnalyticsSkeleton />
      ) : (
        <div className="analytics-content">
          {/* Pie Chart with Total Companies Box */}
          <div className="chart-panel chart-panel--with-total">
            <h3>Company Distribution</h3>
            <div
              className="total-users-box"
              onClick={() => setSelectedCategory("total")}
            >
              Total: {totalCompanyCount.toLocaleString()}
            </div>
            <div className="chart-wrapper">
              <Pie data={pieData} options={pieOptions} />
            </div>
          </div>

          {/* Dynamic Chart: Line for Total, Bar for others */}
          <div className="chart-panel">
            <h3>Trend: {selectedCategoryLabel}</h3>
            <div className="chart-wrapper">
              {timeChartData.labels.length > 0 ? (
                selectedCategory === "total" ? (
                  <Line data={timeChartData} options={lineOptions} />
                ) : (
                  <Bar data={timeChartData} options={barOptions} />
                )
              ) : (
                <div className="no-data">No data in selected period</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}